import { useRef, useState, type KeyboardEvent } from 'react';
import {
  useForm,
  type ControllerRenderProps,
  type FieldValues,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  toast,
} from '@kognitos/lattice';

import {
  V1OAuthGrantType,
  type V1OAuthApp,
} from '@/generated/hooks/gringotts/models';
import { CredentialSensitiveValueInput } from '@/modules/integrations/components/CredentialSensitiveValueInput';
import { captureException } from '@/modules/sentry';
import i18n from '@/shared/i18n';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { getNoAutofillProps } from '@/shared/utils';

import { AdvancedSection } from './AdvancedSection';

import { useRegisterOAuthApp } from '../hooks/use-register-oauth-app';
import { ECredentialScope } from '../types';
import { isCredentialNameConflict } from '../utils';

const REGISTER_OAUTH_APP_FORM_ID = 'register-oauth-app-form';

/** Grant an app is registered for. */
export enum EOAuthGrant {
  AuthorizationCode = 'authorization_code',
  ClientCredentials = 'client_credentials',
}

/** Values the dialog starts from, e.g. a book's OAuth settings. */
export interface IRegisterOAuthAppDefaults {
  displayName?: string;
  grant?: EOAuthGrant;
  authorizeUrl?: string;
  tokenUrl?: string;
  scopes?: string[];
  /**
   * Parameter the provider expects the scopes under instead of `scope`
   * (Slack: `user_scope`). The scopes are then sent as that authorize
   * parameter.
   */
  scopeParameter?: string;
  /** Arguments each connection supplies when signing in (e.g. a tenant). */
  connectionArguments?: { id: string; label: string }[];
}

/** Props for {@link RegisterOAuthAppDialog}. */
export interface IRegisterOAuthAppDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the app once registered. */
  onRegistered?: (app: V1OAuthApp) => void;
  /** The secret type the app signs in credentials for. */
  secretTypeName: string;
  defaults?: IRegisterOAuthAppDefaults;
  /** Keep the grant from `defaults` (it's dictated by the auth method). */
  lockGrant?: boolean;
  /** Whether the user may register organization-wide apps. */
  canChooseScope: boolean;
}

const required = (key: string) =>
  z
    .string()
    .trim()
    .min(1, i18n.t(key, { ns: 'credentials' }));

// A prefix check: URLs may hold placeholders like {TENANT_ID}.
const isHttpsUrl = (value: string) => /^https:\/\/\S+$/i.test(value.trim());

const registerOAuthAppSchema = () =>
  z
    .object({
      displayName: required('credentials.oauthApp.validation.nameRequired'),
      grant: z.enum([
        EOAuthGrant.AuthorizationCode,
        EOAuthGrant.ClientCredentials,
      ]),
      authorizeUrl: z.string(),
      tokenUrl: required('credentials.oauthApp.validation.tokenUrlRequired'),
      clientId: required('credentials.oauthApp.validation.clientIdRequired'),
      clientSecret: required(
        'credentials.oauthApp.validation.clientSecretRequired',
      ),
      scope: z.enum([
        ECredentialScope.Workspace,
        ECredentialScope.Organization,
      ]),
    })
    // Authorization code needs an authorize endpoint; client credentials doesn't.
    .refine(
      data =>
        data.grant !== EOAuthGrant.AuthorizationCode ||
        !!data.authorizeUrl.trim(),
      {
        message: i18n.t(
          'credentials.oauthApp.validation.authorizeUrlRequired',
          {
            ns: 'credentials',
          },
        ),
        path: ['authorizeUrl'],
      },
    )
    .refine(
      data =>
        data.grant !== EOAuthGrant.AuthorizationCode ||
        !data.authorizeUrl.trim() ||
        isHttpsUrl(data.authorizeUrl),
      {
        message: i18n.t('credentials.oauthApp.validation.httpsUrl', {
          ns: 'credentials',
        }),
        path: ['authorizeUrl'],
      },
    )
    .refine(data => !data.tokenUrl.trim() || isHttpsUrl(data.tokenUrl), {
      message: i18n.t('credentials.oauthApp.validation.httpsUrl', {
        ns: 'credentials',
      }),
      path: ['tokenUrl'],
    });

type TRegisterOAuthAppValues = z.infer<
  ReturnType<typeof registerOAuthAppSchema>
>;

/** Parses a whitespace/comma-separated scope string into a unique list. */
const parseScopes = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(/[\s,]+/)
        .map(scope => scope.trim())
        .filter(Boolean),
    ),
  );

/** One row in a {@link KeyValueEditor}; `rowId` is a stable React key. */
interface IKvRow {
  rowId: number;
  key: string;
  value: string;
}

/** Repeatable key/value rows, for fixed arguments and extra authorize params. */
function KeyValueEditor({
  rows,
  onRowsChange,
  keyPlaceholder,
  valuePlaceholder,
  addLabel,
  removeLabel,
}: {
  rows: IKvRow[];
  onRowsChange: (rows: IKvRow[]) => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
  addLabel: string;
  removeLabel: string;
}) {
  const idRef = useRef(Math.max(0, ...rows.map(row => row.rowId + 1)));
  const patch = (rowId: number, next: Partial<IKvRow>) =>
    onRowsChange(
      rows.map(row => (row.rowId === rowId ? { ...row, ...next } : row)),
    );
  return (
    <Flex direction="column" gap={2} width="full">
      {rows.map(row => (
        <Flex key={row.rowId} gap={2} align="center" width="full">
          <Input
            value={row.key}
            onChange={event => patch(row.rowId, { key: event.target.value })}
            placeholder={keyPlaceholder}
            className="font-mono flex-1 min-w-0"
          />
          <Input
            value={row.value}
            onChange={event => patch(row.rowId, { value: event.target.value })}
            placeholder={valuePlaceholder}
            className="font-mono flex-1 min-w-0"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={removeLabel}
            onClick={() =>
              onRowsChange(rows.filter(item => item.rowId !== row.rowId))
            }
          >
            <Icon type="X" size="sm" />
          </Button>
        </Flex>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-fit px-1"
        onClick={() =>
          onRowsChange([
            ...rows,
            { rowId: idRef.current++, key: '', value: '' },
          ])
        }
      >
        <Icon type="Plus" size="sm" />
        {addLabel}
      </Button>
    </Flex>
  );
}

/**
 * Registers an OAuth app for a secret type: the provider endpoints and scopes
 * (prefilled from `defaults`), the client id and the write-only client
 * secret. Scopes are entered as chips; fixed arguments and extra authorize
 * parameters live under Advanced.
 *
 * @param props - Component props.
 * @returns The register-OAuth-app dialog.
 */
export function RegisterOAuthAppDialog({
  isOpen,
  onClose,
  onRegistered,
  secretTypeName,
  defaults,
  lockGrant = false,
  canChooseScope,
}: IRegisterOAuthAppDialogProps) {
  const { t } = useTranslation('credentials');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { registerOAuthApp, isPending } = useRegisterOAuthApp(
    organizationId,
    workspaceId,
  );
  const [scopeList, setScopeList] = useState<string[]>(defaults?.scopes ?? []);
  const [scopeInput, setScopeInput] = useState('');
  const [constantRows, setConstantRows] = useState<IKvRow[]>([]);
  const [paramRows, setParamRows] = useState<IKvRow[]>([]);

  const form = useForm<TRegisterOAuthAppValues>({
    resolver: zodResolver(registerOAuthAppSchema()),
    defaultValues: {
      displayName: defaults?.displayName ?? '',
      grant: defaults?.grant ?? EOAuthGrant.AuthorizationCode,
      authorizeUrl: defaults?.authorizeUrl ?? '',
      tokenUrl: defaults?.tokenUrl ?? '',
      clientId: '',
      clientSecret: '',
      scope: ECredentialScope.Workspace,
    },
  });
  const grant = form.watch('grant');

  const addScopes = (raw: string) => {
    const parsed = parseScopes(raw);
    if (parsed.length === 0) return;
    setScopeList(current => Array.from(new Set([...current, ...parsed])));
    setScopeInput('');
  };
  const removeScope = (scope: string) =>
    setScopeList(current => current.filter(item => item !== scope));

  // Enter/comma add the typed scope as a chip instead of submitting the form.
  const handleScopeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addScopes(scopeInput);
    }
  };

  const handleSubmit = async (values: TRegisterOAuthAppValues) => {
    // Fold in anything still typed but not yet chipped.
    const scopes = Array.from(
      new Set([...scopeList, ...parseScopes(scopeInput)]),
    );
    const isAuthorizationCode = values.grant === EOAuthGrant.AuthorizationCode;
    const extraParams = Object.fromEntries(
      paramRows
        .map(row => [row.key.trim(), row.value] as const)
        .filter(([key]) => key),
    );
    if (defaults?.scopeParameter && scopes.length > 0) {
      extraParams[defaults.scopeParameter] = scopes.join(' ');
    }
    const connectionArguments = (defaults?.connectionArguments ?? []).map(
      argument => ({ id: argument.id, label: argument.label, required: true }),
    );
    const constantArguments = constantRows
      .map(row => ({ id: row.key.trim(), constant: row.value }))
      .filter(row => row.id)
      .map(row => ({ ...row, label: row.id, required: false }));
    const oauthArguments = [...connectionArguments, ...constantArguments];

    try {
      const app = await registerOAuthApp({
        scope: values.scope,
        app: {
          display_name: values.displayName.trim(),
          secret_type: secretTypeName,
          credentials: {
            client_id: values.clientId.trim(),
            client_secret: values.clientSecret,
          },
          config: {
            authorize_url: isAuthorizationCode
              ? values.authorizeUrl
              : undefined,
            token_url: values.tokenUrl,
            scopes: defaults?.scopeParameter ? [] : scopes,
            grant_type: isAuthorizationCode
              ? V1OAuthGrantType.OAUTH_GRANT_TYPE_AUTHORIZATION_CODE
              : V1OAuthGrantType.OAUTH_GRANT_TYPE_CLIENT_CREDENTIALS,
            arguments: oauthArguments.length > 0 ? oauthArguments : undefined,
            extra_authorize_params:
              Object.keys(extraParams).length > 0 ? extraParams : undefined,
          },
        },
      });
      toast.success(t('credentials.oauthApp.registered.toast'));
      onRegistered?.(app);
      onClose();
    } catch (error) {
      if (isCredentialNameConflict(error)) {
        form.setError('displayName', {
          message: t('credentials.oauthApp.validation.nameTaken'),
        });
        return;
      }
      toast.error(t('credentials.oauthApp.registerFailed'));
      captureException(error as Error, {
        tags: { module: 'credentials', action: 'registerOAuthApp' },
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      {/* Same width as the connect dialog it opens over; only the form scrolls. */}
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('credentials.oauthApp.registerTitle')}</DialogTitle>
          <DialogDescription>
            {t('credentials.oauthApp.registerDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6">
          <Form {...form}>
            <form
              id={REGISTER_OAUTH_APP_FORM_ID}
              onSubmit={event => {
                // The dialog may sit inside another form; keep this submit local.
                event.stopPropagation();
                void form.handleSubmit(handleSubmit)(event);
              }}
              className="flex flex-col gap-5"
            >
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('credentials.oauthApp.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} {...getNoAutofillProps()} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {!lockGrant && (
                <FormField
                  control={form.control}
                  name="grant"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('credentials.oauthApp.grant.label')}
                      </FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={EOAuthGrant.AuthorizationCode}>
                            {t('credentials.oauthApp.grant.authorizationCode')}
                          </SelectItem>
                          <SelectItem value={EOAuthGrant.ClientCredentials}>
                            {t('credentials.oauthApp.grant.clientCredentials')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              )}
              {grant === EOAuthGrant.AuthorizationCode && (
                <FormField
                  control={form.control}
                  name="authorizeUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('credentials.oauthApp.authorizeUrl')}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} className="font-mono" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="tokenUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('credentials.oauthApp.tokenUrl')}</FormLabel>
                    <FormControl>
                      <Input {...field} className="font-mono" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('credentials.oauthApp.clientId')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        {...getNoAutofillProps()}
                        className="font-mono"
                        data-testid="register-oauth-app-client-id"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientSecret"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('credentials.oauthApp.clientSecret')}
                    </FormLabel>
                    <FormControl>
                      <CredentialSensitiveValueInput
                        field={
                          field as unknown as ControllerRenderProps<FieldValues>
                        }
                        data-testid="register-oauth-app-client-secret"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormItem>
                <FormLabel>{t('credentials.oauthApp.scopes')}</FormLabel>
                <Input
                  value={scopeInput}
                  onChange={event => setScopeInput(event.target.value)}
                  onKeyDown={handleScopeKeyDown}
                  onBlur={() => addScopes(scopeInput)}
                  placeholder={t('credentials.oauthApp.scopesPlaceholder')}
                />
                {defaults?.scopeParameter && (
                  <FormDescription>
                    {t('credentials.oauthApp.scopesSentAs', {
                      parameter: defaults.scopeParameter,
                    })}
                  </FormDescription>
                )}
                {scopeList.length > 0 && (
                  <Flex gap={2} className="mt-2 flex-wrap">
                    {scopeList.map(scope => (
                      <Badge
                        key={scope}
                        variant="secondary"
                        className="font-mono gap-1"
                      >
                        {scope}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label={t('credentials.oauthApp.removeScope')}
                          onClick={() => removeScope(scope)}
                        >
                          <Icon type="X" size="xs" />
                        </Button>
                      </Badge>
                    ))}
                  </Flex>
                )}
              </FormItem>

              <AdvancedSection>
                <Flex direction="column" gap={4} width="full">
                  <FormItem>
                    <FormLabel>
                      {t('credentials.oauthApp.constants.label')}
                    </FormLabel>
                    <Text level="xSmall" color="muted">
                      {t('credentials.oauthApp.constants.hint')}
                    </Text>
                    <KeyValueEditor
                      rows={constantRows}
                      onRowsChange={setConstantRows}
                      keyPlaceholder="TENANT_ID"
                      valuePlaceholder={t(
                        'credentials.oauthApp.constants.valuePlaceholder',
                      )}
                      addLabel={t('credentials.oauthApp.constants.add')}
                      removeLabel={t('credentials.oauthApp.constants.remove')}
                    />
                  </FormItem>
                  <FormItem>
                    <FormLabel>
                      {t('credentials.oauthApp.extraParams.label')}
                    </FormLabel>
                    <KeyValueEditor
                      rows={paramRows}
                      onRowsChange={setParamRows}
                      keyPlaceholder="prompt"
                      valuePlaceholder="consent"
                      addLabel={t('credentials.oauthApp.extraParams.add')}
                      removeLabel={t('credentials.oauthApp.extraParams.remove')}
                    />
                  </FormItem>
                  {canChooseScope && (
                    <FormField
                      control={form.control}
                      name="scope"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('credentials.create.scope.label')}
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              value={field.value}
                              onValueChange={field.onChange}
                              className="flex flex-col gap-2"
                            >
                              <Flex align="center" gap={2}>
                                <RadioGroupItem
                                  value={ECredentialScope.Workspace}
                                  id="register-oauth-app-scope-workspace"
                                />
                                <Label htmlFor="register-oauth-app-scope-workspace">
                                  {t('credentials.create.scope.workspace')}
                                </Label>
                              </Flex>
                              <Flex align="center" gap={2}>
                                <RadioGroupItem
                                  value={ECredentialScope.Organization}
                                  id="register-oauth-app-scope-organization"
                                />
                                <Label htmlFor="register-oauth-app-scope-organization">
                                  {t('credentials.create.scope.organization')}
                                </Label>
                              </Flex>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                </Flex>
              </AdvancedSection>
            </form>
          </Form>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('credentials.oauthApp.cancel')}
          </Button>
          <Button
            type="submit"
            form={REGISTER_OAUTH_APP_FORM_ID}
            disabled={isPending}
            isLoading={isPending}
          >
            {t('credentials.oauthApp.register')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
